import React, { memo } from "react"
import { Handle, Position, NodeProps } from "@xyflow/react"
import {
  CHECK_KIND_GROUP,
  CHECK_KIND_LEAF,
  CheckExpression,
  ConditionNodeData,
  OPERATOR_AND
} from "lib/types/workflow"
import { OTHERWISE, THEN } from "../../branchKind"

import {
  NodeContainer,
  NodeHeader,
  NodeBody,
  CheckInfo
} from "./ConditionNode.styled"
import {
  StatusBadge,
  FailedMessageBlock,
  statusIcon
} from "./ExecutionOverlay.styled"
import TerminalAddButton from "./TerminalAddButton"

const formatCheckType = (checkType: string): string => {
  return checkType
    .replace(/_/g, " ")
    .toLowerCase()
    .replace(/\b\w/g, (c) => c.toUpperCase())
}

const BILLING_ADDRESS_EMAIL = "BILLING_ADDRESS_EMAIL"

const countLeaves = (expr: CheckExpression): number =>
  expr.kind === CHECK_KIND_LEAF
    ? 1
    : expr.children.reduce((n, c) => n + countLeaves(c), 0)

const getCheckInfoText = (d: ConditionNodeData): string => {
  if (d.expression) {
    const total = countLeaves(d.expression)
    if (d.expression.kind === CHECK_KIND_GROUP) {
      const operatorLabel = d.expression.operator === OPERATOR_AND ? "All of" : "Any of"
      return `${operatorLabel} ${total} ${total === 1 ? "check" : "checks"}`
    }
    return `${total} ${total === 1 ? "check" : "checks"}`
  }
  if (d.checkType === "CUSTOM_HTTP") {
    return `HTTP: ${d.endpoint && d.endpoint.length > 30 ? `${d.endpoint.substring(0, 30)}...` : (d.endpoint || "—")}`
  }
  if (d.checkType === BILLING_ADDRESS_EMAIL) {
    const n = d.values?.length ?? 0
    return `Billing email in list (${n} ${n === 1 ? "email" : "emails"})`
  }
  return `${formatCheckType(d.checkType)} ${d.comparison.toLowerCase()} ${d.values.join(", ")}`
}

const ConditionNode = memo(({ id, data }: NodeProps) => {
  const d = data as ConditionNodeData
  const terminals = d._terminalHandles ?? []
  const status = d._executionStatus
  return (
    <NodeContainer $skipped={status === "skipped"}>
      <Handle type="target" position={Position.Top} id="input" />
      <NodeHeader>Condition</NodeHeader>
      <NodeBody>{d.label}</NodeBody>
      <CheckInfo>{getCheckInfoText(d)}</CheckInfo>
      {d._failedMessage && (
        <FailedMessageBlock>{d._failedMessage}</FailedMessageBlock>
      )}
      <Handle
        type="source"
        position={Position.Bottom}
        id="true-output"
        style={{ left: "25%" }}
      />
      <Handle
        type="source"
        position={Position.Bottom}
        id="false-output"
        style={{ left: "75%" }}
      />
      {terminals.includes("true-output") && d._onAddAtTerminal && (
        <TerminalAddButton
          left="25%"
          branchLabel={THEN}
          onClick={() => d._onAddAtTerminal?.(id, "true-output")}
          highlighted={d._activeTerminalDropHandle === "true-output"}
        />
      )}
      {terminals.includes("false-output") && d._onAddAtTerminal && (
        <TerminalAddButton
          left="75%"
          branchLabel={OTHERWISE}
          onClick={() => d._onAddAtTerminal?.(id, "false-output")}
          highlighted={d._activeTerminalDropHandle === "false-output"}
        />
      )}
      {status && (
        <StatusBadge $status={status}>{statusIcon(status)}</StatusBadge>
      )}
    </NodeContainer>
  )
})

ConditionNode.displayName = "ConditionNode"

export default ConditionNode
